/**
 * /app/$tenantSlug/payroll/filings/$id — G_FILE filing detail.
 *
 * Shows the generated filing's metadata + a download button (ECR/ESI/24Q)
 * or per-employee Part-B download list (Form-16), a "validate before you
 * file" hint naming the exact govt utility, and for 24Q an editable
 * Challans sub-panel (pre-freeze). Figures are immutable (§128(1)).
 */

import { useState } from "react";
import {
  createFileRoute,
  Link,
  useParams,
} from "@tanstack/react-router";
import { ArrowLeft, Download, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toApiError } from "@/lib/api";
import {
  useFiling,
  useChallans,
  useCreateChallan,
  useDeleteChallan,
} from "@/hooks/use-filings";
import {
  downloadFilingFile,
  triggerDownload,
  FILING_KIND_LABELS,
} from "@/lib/api/filings";
import type { FilingDetail } from "@/lib/api/filings";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/payroll/filings/$id",
)({
  component: FilingDetailRoute,
});

function FilingDetailRoute() {
  const { tenantSlug, id } = useParams({ strict: false }) as {
    tenantSlug: string;
    id: string;
  };
  const filingId = parseInt(id, 10);
  const q = useFiling(filingId);

  return (
    <PayrollModuleShell title="Filing detail">
      <Link
        to="/app/$tenantSlug/payroll/filings"
        params={{ tenantSlug }}
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to filings
      </Link>

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {q.error && (
        <p className="text-sm text-destructive">{toApiError(q.error).message}</p>
      )}
      {q.data && <FilingBody detail={q.data} />}
    </PayrollModuleShell>
  );
}

function FilingBody({ detail }: Readonly<{ detail: FilingDetail }>) {
  const { filing } = detail;
  const [downloading, setDownloading] = useState(false);

  async function onDownload() {
    setDownloading(true);
    try {
      const blob = await downloadFilingFile(filing.id, { download: true });
      triggerDownload(
        blob,
        `${filing.kind}-${filing.period_year}-${filing.period_index}.txt`,
      );
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">
                {FILING_KIND_LABELS[filing.kind]} — {filing.period_year}
                {filing.period_index > 0 ? ` / ${filing.period_index}` : ""}
              </CardTitle>
              <CardDescription>
                Generated {new Date(filing.generated_at).toLocaleString()}
                {filing.revision_no > 0 ? ` · revision ${filing.revision_no}` : ""}
              </CardDescription>
            </div>
            <Badge variant={filing.frozen ? "secondary" : "outline"}>
              {filing.frozen ? "Filed (frozen)" : "Generated"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert>
            <AlertTitle>Validate before you file</AlertTitle>
            <AlertDescription>{validateHint(filing.kind)}</AlertDescription>
          </Alert>
          {filing.kind !== "form16" ? (
            <Button onClick={onDownload} disabled={downloading} className="gap-1.5">
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Download file
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              Form-16 is a per-employee PDF. Use the snapshot table below to
              download each employee's Part B; obtain Part A from TRACES and
              merge before issuing.
            </p>
          )}
        </CardContent>
      </Card>

      {filing.kind === "tds_24q_fvu" && (
        <ChallansPanel detail={detail} />
      )}
    </div>
  );
}

function ChallansPanel({ detail }: Readonly<{ detail: FilingDetail }>) {
  const { filing } = detail;
  const year = filing.period_year;
  const quarter = filing.period_index;
  const q = useChallans(year, quarter);
  const createMut = useCreateChallan();
  const delMut = useDeleteChallan();

  const [bsr, setBsr] = useState("");
  const [serial, setSerial] = useState("");
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");

  async function onAdd() {
    try {
      await createMut.mutateAsync({
        period_year: year,
        period_quarter: quarter,
        bsr_code: bsr.trim(),
        challan_serial: serial.trim(),
        deposit_date: date,
        tds_paise: Math.round(parseFloat(amount || "0") * 100),
      });
      setBsr("");
      setSerial("");
      setDate("");
      setAmount("");
      toast.success("Challan added.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">TDS challans — Q{quarter} {year}</CardTitle>
        <CardDescription>
          Enter the bank challans you used to deposit the deducted TDS. These
          become the CD records in the 24Q FVU file. Editable until the file is
          downloaded (frozen).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <div>
            <Label htmlFor="bsr">BSR code</Label>
            <Input id="bsr" value={bsr} onChange={(e) => setBsr(e.target.value)} placeholder="0123456" />
          </div>
          <div>
            <Label htmlFor="serial">Serial</Label>
            <Input id="serial" value={serial} onChange={(e) => setSerial(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="date">Deposit date</Label>
            <Input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="amount">TDS (₹)</Label>
            <Input id="amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button onClick={onAdd} disabled={createMut.isPending} className="gap-1 w-full">
              <Plus className="h-4 w-4" /> Add
            </Button>
          </div>
        </div>

        <div className="divide-y rounded border">
          {(q.data?.items ?? []).length === 0 && (
            <p className="p-3 text-sm text-muted-foreground">No challans yet.</p>
          )}
          {(q.data?.items ?? []).map((c) => (
            <div key={c.id} className="flex items-center justify-between p-3 text-sm">
              <span>
                BSR {c.bsr_code} · #{c.challan_serial} · {c.deposit_date} ·
                ₹{(c.tds_paise / 100).toLocaleString("en-IN")}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => delMut.mutate(c.id)}
                disabled={delMut.isPending}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function validateHint(kind: string): string {
  switch (kind) {
    case "ecr":
      return "Upload this .txt on the EPFO Unified Portal (ECR Upload). The portal returns a TRRN and a draft challan.";
    case "esi_monthly":
      return "Upload this CSV via the ESIC employer portal Monthly Contribution bulk upload; the portal computes the 0.75% / 3.25% contributions.";
    case "tds_24q_fvu":
      return "Open this .txt in the Protean RPU, then run the FVU. Upload the resulting .fvu from your TRACES/e-filing login.";
    case "form16":
      return "This is Part B only. Download Part A from TRACES after the 24Q is processed and merge before issuing to each employee.";
    default:
      return "";
  }
}
